// packages/engine/src/timeline.ts
//
// The timeline is DERIVED, never declared.
//
// Whatever markers the lifecycle decorations mention ARE the timeline: collect
// every distinct one written in the model, order them, and that ordered list is
// the axis. There is no release registry, no date anchoring and no special
// syntax — `2026-08-17`, `2026-Q3`, `Q3'26`, `v1`, `1.5.2` and `v26.17.2` are
// all just markers, ordered the way a reader would order them.
//
// A "time point" downstream is therefore an ORDINAL on that axis, not an
// instant: `LifecycleWindow` bounds, scrub points and the `@lifecycle` builtin
// all compare ordinals. That keeps every comparison exact — no marker is ever
// mapped onto a date it did not name — and lets a pure-version roadmap and a
// pure-date roadmap use one mechanism.
//
// `now` is the one point that is not a marker. It sits BETWEEN markers, at the
// boundary between what has already happened and what has not: a date-shaped
// marker whose interval has started is past, and everything else (a future date,
// any version, any free-text label) has not happened yet. On a pure-version
// roadmap that puts `now` before every marker, which is the correct as-is
// reading — nothing on the roadmap has shipped.

// =============================================================================
// Marker classification
// =============================================================================

/**
 * The three shapes a marker can take. Ordering across families is defined but
 * arbitrary-by-nature, so a model that mixes them gets an advisory: dates are
 * absolute points, versions are roadmap positions, and free text is a label.
 */
export type MarkerFamily = "date" | "version" | "text";

interface DateKey {
  readonly family: "date";
  /** Start of the interval the marker names, in ms since epoch (UTC). */
  readonly at: number;
  /** 0 year · 1 half · 2 quarter · 3 month · 4 day — coarser sorts first when
   *  two markers start at the same instant, because it contains the finer one. */
  readonly gran: number;
}

interface VersionKey {
  readonly family: "version";
  readonly segs: readonly number[];
  /** Semver-style prerelease tail; a version WITH one precedes the same version
   *  without (`1.0.0-rc1` < `1.0.0`). */
  readonly tail: string;
}

interface TextKey {
  readonly family: "text";
  readonly norm: string;
}

export type MarkerKey = DateKey | VersionKey | TextKey;

const FAMILY_RANK: Record<MarkerFamily, number> = { date: 0, version: 1, text: 2 };

const QUARTER_MONTH = [0, 3, 6, 9];

/**
 * Parse a date-shaped marker to the start of the interval it names.
 *
 * Granularity is part of the marker: `2026`, `2026-H1`, `2026-Q3`, `2026-08` and
 * `2026-08-17` all parse, each meaning the whole interval it names. Quarters and
 * halves are accepted in the spellings people actually write them in — leading
 * or trailing, separated by `-`, `/`, a space or an apostrophe.
 *
 * Returns undefined when the text is not a date.
 */
export function parseDateLiteral(text: string): { at: number; gran: number } | undefined {
  const t = text.trim();

  // 2026
  let m = /^(\d{4})$/.exec(t);
  if (m) return inRange(Number(m[1]), 1900, 2999) ? { at: Date.UTC(Number(m[1]), 0, 1), gran: 0 } : undefined;

  // 2026-Q3 · 2026Q3 · 2026/Q3 · 2026-H1
  m = /^(\d{4})[-/ ']?([QqHh])([1-4])$/.exec(t);
  if (m) {
    const y = Number(m[1]);
    const half = m[2]!.toLowerCase() === "h";
    const n = Number(m[3]);
    if (!inRange(y, 1900, 2999) || (half && n > 2)) return undefined;
    return { at: Date.UTC(y, half ? (n - 1) * 6 : QUARTER_MONTH[n - 1]!, 1), gran: half ? 1 : 2 };
  }

  // Q3 2026 · Q3'26 · 3Q2026 · H1-2026
  m = /^([QqHh])?([1-4])([QqHh])?[-/ ']?(\d{2}|\d{4})$/.exec(t);
  if (m && (m[1] ?? m[3])) {
    const half = (m[1] ?? m[3])!.toLowerCase() === "h";
    const n = Number(m[2]);
    const y = expandYear(Number(m[4]), m[4]!.length);
    if (y === undefined || (half && n > 2)) return undefined;
    return { at: Date.UTC(y, half ? (n - 1) * 6 : QUARTER_MONTH[n - 1]!, 1), gran: half ? 1 : 2 };
  }

  // 2026-08 · 2026/08
  m = /^(\d{4})[-/](\d{2})$/.exec(t);
  if (m) {
    const [y, mo] = [Number(m[1]), Number(m[2])];
    if (!inRange(y, 1900, 2999) || !inRange(mo, 1, 12)) return undefined;
    return { at: Date.UTC(y, mo - 1, 1), gran: 3 };
  }

  // 2026-08-17 · 2026/08/17
  m = /^(\d{4})[-/](\d{2})[-/](\d{2})$/.exec(t);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (!inRange(y, 1900, 2999) || !inRange(mo, 1, 12) || !inRange(d, 1, 31)) return undefined;
    const at = Date.UTC(y, mo - 1, d);
    // Reject an overflowing day (`2026-02-31`), which `Date.UTC` would silently
    // roll into the next month rather than refuse.
    return new Date(at).getUTCMonth() === mo - 1 ? { at, gran: 4 } : undefined;
  }

  return undefined;
}

/**
 * Whether `text` is SHAPED like a date — four leading digits, or a quarter/half
 * prefix — without committing to it being a valid one.
 *
 * This is what separates "the author wrote a malformed date" from "the author
 * wrote a version name": `v3` is a version, `2026-13-99` is a broken date, and
 * only the second is worth a diagnostic.
 */
export function looksLikeDate(text: string): boolean {
  const t = text.trim();
  // A bare four-digit number is a year only inside a plausible range; outside
  // it, `1024` is a build number, and calling it a broken year refused a marker
  // there is no legal way to write.
  const bare = /^(\d{4})$/.exec(t);
  if (bare) return inRange(Number(bare[1]), 1900, 2999);
  // What FOLLOWS the year has to be date-shaped too — a digit, optionally
  // behind a quarter/half letter. `2026-13-99` is a date attempt worth
  // reporting; `2026-rc1` is a CalVer prerelease and `2026.1` is CalVer (`.` is
  // deliberately not a date separator here), and neither is a broken anything.
  return /^\d{4}[-/ ']?[QqHh]?\d/.test(t) || /^[QqHh][1-4][-/ ']?\d/.test(t);
}

/** Two-digit years read as 2000..2069 / 1970..1999 — the usual window. */
function expandYear(n: number, digits: number): number | undefined {
  if (digits === 4) return inRange(n, 1900, 2999) ? n : undefined;
  return n <= 69 ? 2000 + n : 1900 + n;
}

function inRange(n: number, lo: number, hi: number): boolean {
  return n >= lo && n <= hi;
}

const VERSION_RE = /^[vV]?(\d+(?:[._]\d+)*)(?:[-+](.+))?$/;

/** Classify a marker into its comparable key. Date shapes win over version
 *  shapes, because `2026-08` is a month and not a two-segment version. */
export function markerKey(raw: string): MarkerKey {
  const t = raw.trim();
  const d = parseDateLiteral(t);
  if (d) return { family: "date", at: d.at, gran: d.gran };
  // A marker that MEANT to be a date and is not one (`2026-13-99`) is not
  // secretly a version either — `2026` plus a `-13-99` prerelease tail would
  // file a typo in among the real versions. Leave it as text, where the
  // malformed-date diagnostic points at it.
  if (looksLikeDate(t)) return { family: "text", norm: t.toLowerCase() };
  const m = VERSION_RE.exec(t);
  if (m) {
    return {
      family: "version",
      segs: m[1]!.split(/[._]/).map(Number),
      tail: m[2] ?? "",
    };
  }
  return { family: "text", norm: t.toLowerCase() };
}

export function markerFamily(raw: string): MarkerFamily {
  return markerKey(raw).family;
}

/**
 * The total order the timeline is sorted by.
 *
 * Within a family the comparison is the natural one — chronological for dates,
 * segment-wise numeric for versions (so `1.9` precedes `1.10`), case-insensitive
 * for text. Across families it is date → version → text, which also matches the
 * `now` rule: an undated marker has not happened yet. The raw string breaks any
 * remaining tie so the order is total and the sort deterministic.
 */
export function compareMarkers(a: string, b: string): number {
  const ka = markerKey(a);
  const kb = markerKey(b);
  const rank = FAMILY_RANK[ka.family] - FAMILY_RANK[kb.family];
  if (rank !== 0) return rank;
  if (ka.family === "date" && kb.family === "date") {
    return ka.at - kb.at || ka.gran - kb.gran || tie(a, b);
  }
  if (ka.family === "version" && kb.family === "version") {
    return compareSegments(ka.segs, kb.segs) || compareTails(ka.tail, kb.tail) || tie(a, b);
  }
  // Free text reads its digit runs numerically too, so `phase2` precedes
  // `phase10`. Comparing the raw strings made this the one family that ordered
  // the way a machine sorts rather than the way a reader does.
  return compareRuns((ka as TextKey).norm, (kb as TextKey).norm) || tie(a, b);
}

function tie(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareSegments(a: readonly number[], b: readonly number[]): number {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    // A missing segment reads as 0, so `1.5` and `1.5.0` are the same version.
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * Prerelease ordering: a tail sorts BEFORE no tail (`2.0.0-rc1` precedes
 * `2.0.0`), and within a tail digit runs compare NUMERICALLY.
 *
 * Numerically rather than by strict semver's alphanumeric-identifier rule,
 * because the rule that matters here is the one a reader applies: `rc2` before
 * `rc10`, not after it.
 */
function compareTails(a: string, b: string): number {
  if (a === b) return 0;
  if (a === "") return 1;
  if (b === "") return -1;
  return compareRuns(a, b);
}

/**
 * Compare two strings run by run, digits numerically and everything else
 * lexicographically — the ordering a reader applies to `phase2` vs `phase10`.
 *
 * A total order: digit code points are contiguous, so a non-digit run can never
 * sort BETWEEN two numeric runs, which is what would break transitivity.
 */
function compareRuns(a: string, b: string): number {
  const pa = splitRuns(a);
  const pb = splitRuns(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i];
    const y = pb[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    const nx = /^\d+$/.test(x) ? Number(x) : undefined;
    const ny = /^\d+$/.test(y) ? Number(y) : undefined;
    if (nx !== undefined && ny !== undefined) {
      if (nx !== ny) return nx - ny;
      continue;
    }
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/** Split into alternating digit / non-digit runs, so `rc10` reads as `rc` + 10. */
function splitRuns(s: string): string[] {
  return s.match(/\d+|\D+/g) ?? [];
}

// =============================================================================
// The axis
// =============================================================================

/**
 * The ordered marker axis for a model.
 *
 * `markers` is sorted and deduplicated; a marker's ordinal is its index. `now`
 * is a half-integer sitting between the last past marker and the first future
 * one, so it never collides with a marker's own ordinal.
 */
export interface Timeline {
  readonly markers: readonly string[];
  readonly index: ReadonlyMap<string, number>;
  readonly now: number;
  /** Which families the axis mixes — more than one earns an advisory. */
  readonly families: readonly MarkerFamily[];
}

export const EMPTY_TIMELINE: Timeline = {
  markers: [],
  index: new Map(),
  now: -0.5,
  families: [],
};

/**
 * Build the axis from every marker the model mentions.
 *
 * `nowMs` is injectable so goldens and tests are not hostage to the wall clock;
 * it defaults to the real current time.
 */
export function buildTimeline(
  markers: Iterable<string>,
  opts: { readonly nowMs?: number } = {},
): Timeline {
  const seen = new Set<string>();
  for (const raw of markers) {
    const t = raw.trim();
    if (t) seen.add(t);
  }
  if (seen.size === 0) return EMPTY_TIMELINE;
  const sorted = [...seen].sort(compareMarkers);
  const index = new Map<string, number>();
  sorted.forEach((m, i) => index.set(m, i));

  const nowMs = opts.nowMs ?? Date.now();
  let k = sorted.length;
  for (let i = 0; i < sorted.length; i++) {
    if (isFutureMarker(sorted[i]!, nowMs)) {
      k = i;
      break;
    }
  }

  const families: MarkerFamily[] = [];
  for (const m of sorted) {
    const f = markerFamily(m);
    if (!families.includes(f)) families.push(f);
  }

  return { markers: sorted, index, now: k - 0.5, families };
}

/** A marker has not happened yet unless it names a date interval that has
 *  already started. Versions and labels carry no date, so they read as future. */
function isFutureMarker(marker: string, nowMs: number): boolean {
  const k = markerKey(marker);
  return k.family !== "date" || k.at > nowMs;
}

/**
 * The ordinal of a marker on this axis.
 *
 * A marker the model wrote resolves to its exact index. One it did not — a view
 * scrubbing to `at 2026-Q3` when no decoration named that quarter — resolves to
 * a VIRTUAL half-integer at its insertion point, so `at` works for any marker
 * without putting a tick on the axis for it.
 */
export function ordinalOf(timeline: Timeline, marker: string): number {
  const t = marker.trim();
  const exact = timeline.index.get(t);
  if (exact !== undefined) return exact;
  let before = 0;
  for (const m of timeline.markers) {
    if (compareMarkers(m, t) < 0) before++;
    else break;
  }
  return before - 0.5;
}

/** The marker text at an ordinal — exact positions only, so a report can echo
 *  the author's own token instead of a number or a date it never wrote. */
export function labelAt(timeline: Timeline, ordinal: number): string | undefined {
  return Number.isInteger(ordinal) ? timeline.markers[ordinal] : undefined;
}

/**
 * Resolve a view's `at` point to an ordinal.
 *
 * Typed structurally rather than against `ScrubPoint` so this module stays free
 * of the AST; the shapes are the same one.
 */
export function scrubOrdinal(
  timeline: Timeline,
  point: { readonly kind: "now" } | { readonly kind: "marker"; readonly text: string },
): number {
  return point.kind === "now" ? timeline.now : ordinalOf(timeline, point.text);
}
